/**
 * Egress Policy Editor component for configuring network egress rules.
 *
 * @module components/ui/EgressPolicyEditor
 */

import { Link2, Plus, X, ChevronDown, ChevronRight, KeyRound } from 'lucide-react';
import { useState } from 'react';
import { Button } from './Button';
import { Input } from './Input';
import { Select } from './Select';
import { useSecretKeys } from '@/hooks';
import type {
  EgressHostRule,
  EgressPolicyAction,
  EgressPolicyRule,
  EgressPolicyActionType,
  EgressPolicyHeaderOperation,
  EgressPolicyHeaderTransform,
  TrafficInspection,
  SecretResponse,
} from '@/types';

export interface EgressPolicyEditorProps {
  /** The default action for the policy */
  defaultAction: EgressPolicyAction;
  /** Callback when the default action changes */
  onDefaultActionChange: (action: EgressPolicyAction) => void;
  /** The list of host rules */
  hostRules: EgressHostRule[];
  /** Callback when host rules change */
  onHostRulesChange: (rules: EgressHostRule[]) => void;
  /** Rich egress policy rules (Allow/Deny/Transform/Rewrite with headers) */
  rules?: EgressPolicyRule[];
  /** Callback when rich rules change */
  onRulesChange?: (rules: EgressPolicyRule[]) => void;
  /** Whether to show a compact layout (no labels on subsequent rows) */
  compact?: boolean;
  /** Names of connectors attached to this sandbox (shown as read-only info) */
  connectorNames?: string[];
  /** TLS inspection mode — controls which traffic is MITM'd */
  trafficInspection?: TrafficInspection;
  /** Callback when traffic inspection mode changes */
  onTrafficInspectionChange?: (mode: TrafficInspection) => void;
  /** Available secrets for the Secret ID dropdown */
  availableSecrets?: SecretResponse[];
  /** Sandbox group ID for group-scoped secret key lookups */
  sandboxGroupId?: string;
}

const createEmptyHeader = (): EgressPolicyHeaderTransform => ({
  operation: 'Set',
  name: '',
  value: '',
});

/** Determine whether a header is using a secret ref or plain text */
function getValueSource(header: EgressPolicyHeaderTransform): 'plain' | 'secret' {
  return header.valueRef?.secretRef ? 'secret' : 'plain';
}

/**
 * Sub-component: renders the Secret ID dropdown, Key dropdown, and Format input
 * for a single header transform using a secret reference.
 */
function SecretRefFields({
  header,
  availableSecrets,
  sandboxGroupId,
  onChange,
  showLabels,
}: {
  header: EgressPolicyHeaderTransform;
  availableSecrets?: SecretResponse[];
  sandboxGroupId?: string;
  onChange: (h: EgressPolicyHeaderTransform) => void;
  showLabels: boolean;
}) {
  const secretId = header.valueRef?.secretRef?.secretId ?? '';
  const {
    data: secretKeysList,
    isLoading: keysLoading,
    isError: keysError,
  } = useSecretKeys(secretId, sandboxGroupId);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
      <Select
        label={showLabels ? 'Secret ID' : undefined}
        value={secretId}
        onChange={(e) =>
          onChange({
            ...header,
            value: undefined,
            valueRef: {
              secretRef: {
                secretId: e.target.value,
                secretKey: undefined,
                format: header.valueRef?.secretRef?.format,
              },
            },
          })
        }
        placeholder="Select secret..."
        options={
          availableSecrets && availableSecrets.length > 0
            ? availableSecrets.map((s) => ({ value: s.id, label: s.id }))
            : []
        }
      />
      {(!availableSecrets || availableSecrets.length === 0) && (
        <p className="md:col-span-3 -mt-1 text-xs text-amber-600 dark:text-amber-400">
          No secrets available — create one on the Secrets page
        </p>
      )}
      <Select
        label={showLabels ? 'Key' : undefined}
        value={header.valueRef?.secretRef?.secretKey ?? ''}
        onChange={(e) =>
          onChange({
            ...header,
            value: undefined,
            valueRef: {
              secretRef: {
                secretId,
                secretKey: e.target.value || undefined,
                format: header.valueRef?.secretRef?.format,
              },
            },
          })
        }
        placeholder={
          !secretId
            ? 'Select a secret first'
            : keysLoading
              ? 'Loading keys...'
              : keysError
                ? 'Failed to load keys'
                : 'Select key...'
        }
        disabled={!secretId || keysLoading}
        options={secretKeysList ? secretKeysList.map((k) => ({ value: k, label: k })) : []}
      />
      <Input
        label={showLabels ? 'Format (optional)' : undefined}
        placeholder="Bearer {value}"
        value={header.valueRef?.secretRef?.format ?? ''}
        onChange={(e) =>
          onChange({
            ...header,
            value: undefined,
            valueRef: {
              secretRef: {
                secretId,
                secretKey: header.valueRef?.secretRef?.secretKey,
                format: e.target.value || undefined,
              },
            },
          })
        }
        helperText={showLabels ? 'Use {value} as placeholder' : undefined}
      />
    </div>
  );
}

/**
 * A reusable component for editing egress policy settings.
 * Includes a default action selector and a dynamic list of host rules.
 */
export function EgressPolicyEditor({
  defaultAction,
  onDefaultActionChange,
  hostRules,
  onHostRulesChange,
  rules,
  onRulesChange,
  compact = false,
  connectorNames,
  trafficInspection = 'Legacy',
  onTrafficInspectionChange,
  availableSecrets,
  sandboxGroupId,
}: EgressPolicyEditorProps) {
  const [expandedRules, setExpandedRules] = useState<Set<number>>(new Set());

  const handleAddRule = () => {
    onHostRulesChange([...hostRules, { pattern: '', action: undefined }]);
  };

  const handleRemoveRule = (index: number) => {
    onHostRulesChange(hostRules.filter((_, i) => i !== index));
  };

  const handleRuleChange = (index: number, field: keyof EgressHostRule, value: string) => {
    onHostRulesChange(
      hostRules.map((rule, i) => {
        if (i !== index) return rule;
        if (field === 'pattern') {
          return { ...rule, pattern: value };
        }
        // For action field, convert empty string to undefined
        return { ...rule, action: value === '' ? undefined : (value as EgressPolicyAction) };
      })
    );
  };

  const toggleRuleExpanded = (index: number) => {
    setExpandedRules((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const handleAddRichRule = () => {
    if (!onRulesChange) return;
    const defaultType = isPartialMode ? 'Transform' : 'Allow';
    const newRule: EgressPolicyRule = {
      name: '',
      match: { host: '' },
      action: { type: defaultType },
    };
    const newRules = [...(rules ?? []), newRule];
    onRulesChange(newRules);
    setExpandedRules((prev) => new Set(prev).add(newRules.length - 1));
  };

  const handleRemoveRichRule = (index: number) => {
    if (!onRulesChange) return;
    onRulesChange((rules ?? []).filter((_, i) => i !== index));
    setExpandedRules((prev) => {
      const next = new Set<number>();
      for (const idx of prev) {
        if (idx < index) next.add(idx);
        else if (idx > index) next.add(idx - 1);
      }
      return next;
    });
  };

  const updateRichRule = (index: number, updater: (rule: EgressPolicyRule) => EgressPolicyRule) => {
    if (!onRulesChange) return;
    onRulesChange((rules ?? []).map((r, i) => (i === index ? updater(r) : r)));
  };

  const handleAddHeader = (ruleIndex: number) => {
    updateRichRule(ruleIndex, (rule) => ({
      ...rule,
      action: {
        ...rule.action,
        headers: [...(rule.action.headers ?? []), createEmptyHeader()],
      },
    }));
  };

  const handleRemoveHeader = (ruleIndex: number, headerIndex: number) => {
    updateRichRule(ruleIndex, (rule) => ({
      ...rule,
      action: {
        ...rule.action,
        headers: (rule.action.headers ?? []).filter((_, i) => i !== headerIndex),
      },
    }));
  };

  const updateHeader = (
    ruleIndex: number,
    headerIndex: number,
    updater: (h: EgressPolicyHeaderTransform) => EgressPolicyHeaderTransform
  ) => {
    updateRichRule(ruleIndex, (rule) => ({
      ...rule,
      action: {
        ...rule.action,
        headers: (rule.action.headers ?? []).map((h, i) => (i === headerIndex ? updater(h) : h)),
      },
    }));
  };

  const isNoneMode = trafficInspection === 'None';
  const isPartialMode = trafficInspection === 'Partial';

  return (
    <div className="space-y-6">
      {onTrafficInspectionChange && (
        <Select
          label="Traffic Inspection"
          value={trafficInspection}
          onChange={(e) => {
            const mode = e.target.value as TrafficInspection;
            onTrafficInspectionChange(mode);
            if (mode === 'Partial' || mode === 'None') {
              onDefaultActionChange('Allow');
            }
            if (mode === 'None') {
              onHostRulesChange([]);
              onRulesChange?.([]);
            }
            if (mode === 'Partial') {
              // Strip Allow/Deny rules that aren't valid in Partial mode
              onRulesChange?.(
                (rules ?? []).filter(
                  (r) => r.action.type === 'Transform' || r.action.type === 'Rewrite'
                )
              );
              // Strip host rules with explicit Deny action
              onHostRulesChange(hostRules.filter((r) => r.action !== 'Deny'));
            }
          }}
          options={[
            {
              value: 'Legacy',
              label: 'Legacy - Inspect all HTTPS with legacy non-HTTP passthrough',
            },
            { value: 'Partial', label: 'Partial - Inspect only Transform/Rewrite hosts' },
            { value: 'Full', label: 'Full - Inspect all HTTPS, full policy enforcement' },
            { value: 'None', label: 'None - Bypass proxy entirely' },
          ]}
        />
      )}

      <Select
        label="Default Action"
        value={defaultAction}
        onChange={(e) => onDefaultActionChange(e.target.value as EgressPolicyAction)}
        disabled={isNoneMode || isPartialMode}
        options={[
          { value: 'Allow', label: 'Allow - Allow all outbound connections by default' },
          { value: 'Deny', label: 'Deny - Block all outbound connections by default' },
        ]}
      />
      {(isPartialMode || isNoneMode) && (
        <p className="-mt-4 text-xs text-amber-600 dark:text-amber-400">
          Deny is only supported in Legacy or Full traffic inspection mode
        </p>
      )}

      {connectorNames && connectorNames.length > 0 && (
        <div className="space-y-3">
          <div>
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Connector Rules
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              These connectors automatically contribute egress rules. Connector rules take priority
              over host rules and the default action.
            </div>
          </div>
          <div className="space-y-2">
            {connectorNames.map((name) => (
              <div
                key={name}
                className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800"
              >
                <Link2 className="h-4 w-4 text-blue-500 shrink-0" />
                <span className="text-sm font-medium text-blue-700 dark:text-blue-300">{name}</span>
                <span className="ml-auto text-xs text-blue-500 dark:text-blue-400">
                  Auto-generated
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Host Rules</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Add optional overrides for specific host patterns.
            </div>
          </div>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={handleAddRule}
            disabled={isNoneMode}
          >
            Add Rule
          </Button>
        </div>

        {hostRules.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No rules. The default action will apply to all outbound connections.
          </p>
        ) : (
          <div className="space-y-3">
            {hostRules.map((rule, index) => (
              <div
                key={index}
                className="grid grid-cols-1 md:grid-cols-12 gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
              >
                <div className="md:col-span-7">
                  <Input
                    label={!compact || index === 0 ? 'Pattern' : undefined}
                    placeholder="*.example.com"
                    value={rule.pattern}
                    onChange={(e) => handleRuleChange(index, 'pattern', e.target.value)}
                    required
                    helperText={
                      index === hostRules.length - 1
                        ? 'Supports wildcards, e.g., *.github.com'
                        : undefined
                    }
                  />
                </div>
                <div className="md:col-span-4">
                  <Select
                    label={!compact || index === 0 ? 'Action (Optional)' : undefined}
                    value={rule.action ?? ''}
                    onChange={(e) => handleRuleChange(index, 'action', e.target.value)}
                    placeholder="Use default"
                    options={[
                      { value: 'Allow', label: 'Allow', disabled: isPartialMode },
                      { value: 'Deny', label: 'Deny', disabled: isPartialMode },
                    ]}
                  />
                </div>
                <div
                  className={`md:col-span-1 flex items-start justify-end ${!compact || index === 0 ? 'pt-7' : 'pt-1'}`}
                >
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleRemoveRule(index)}
                    title="Remove rule"
                    aria-label="Remove rule"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {onRulesChange && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Advanced Rules
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Rules with Transform and Rewrite support for header injection and request
                redirection. Evaluated after connector rules, in order (first match wins).
              </div>
            </div>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={handleAddRichRule}
              className="whitespace-nowrap"
              disabled={isNoneMode}
            >
              Add Rule
            </Button>
          </div>

          {!rules || rules.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No advanced rules configured.
            </p>
          ) : (
            <div className="space-y-3">
              {rules.map((rule, index) => {
                const isExpanded = expandedRules.has(index);
                const label = rule.name || rule.match.host || `Rule ${index + 1}`;
                return (
                  <div
                    key={index}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                  >
                    <div
                      className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 cursor-pointer"
                      onClick={() => toggleRuleExpanded(index)}
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-gray-500 shrink-0" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-gray-500 shrink-0" />
                      )}
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {label}
                      </span>
                      <span
                        className={`ml-auto text-xs px-2 py-0.5 rounded-full ${
                          rule.action.type === 'Transform'
                            ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                            : rule.action.type === 'Rewrite'
                              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                              : rule.action.type === 'Allow'
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                        }`}
                      >
                        {rule.action.type}
                      </span>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveRichRule(index);
                        }}
                        title="Remove rule"
                        aria-label="Remove rule"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>

                    {isExpanded && (
                      <div className="p-4 space-y-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <Input
                            label="Name (Optional)"
                            placeholder="allow-github"
                            value={rule.name ?? ''}
                            onChange={(e) =>
                              updateRichRule(index, (r) => ({
                                ...r,
                                name: e.target.value || undefined,
                              }))
                            }
                          />
                          <Select
                            label="Action"
                            value={rule.action.type}
                            onChange={(e) =>
                              updateRichRule(index, (r) => {
                                const newType = e.target.value as EgressPolicyActionType;
                                const supportsHeaders =
                                  newType === 'Transform' || newType === 'Rewrite';
                                return {
                                  ...r,
                                  action: {
                                    type: newType,
                                    headers: supportsHeaders
                                      ? (r.action.headers ?? [createEmptyHeader()])
                                      : undefined,
                                    scheme:
                                      newType === 'Rewrite' ? (r.action.scheme ?? '') : undefined,
                                    host: newType === 'Rewrite' ? (r.action.host ?? '') : undefined,
                                    path: newType === 'Rewrite' ? (r.action.path ?? '') : undefined,
                                  },
                                };
                              })
                            }
                            options={
                              isPartialMode
                                ? [
                                    { value: 'Transform', label: 'Transform (inject headers)' },
                                    { value: 'Rewrite', label: 'Rewrite (redirect request)' },
                                  ]
                                : [
                                    { value: 'Allow', label: 'Allow' },
                                    { value: 'Deny', label: 'Deny' },
                                    { value: 'Transform', label: 'Transform (inject headers)' },
                                    { value: 'Rewrite', label: 'Rewrite (redirect request)' },
                                  ]
                            }
                          />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <Input
                            label="Host Pattern"
                            placeholder="*.example.com"
                            value={rule.match.host}
                            onChange={(e) =>
                              updateRichRule(index, (r) => ({
                                ...r,
                                match: { ...r.match, host: e.target.value },
                              }))
                            }
                            required
                            helperText="Supports wildcards, e.g., *.github.com"
                          />
                          <Input
                            label="Path (Optional)"
                            placeholder="/api/*"
                            value={rule.match.path ?? ''}
                            onChange={(e) =>
                              updateRichRule(index, (r) => ({
                                ...r,
                                match: { ...r.match, path: e.target.value || undefined },
                              }))
                            }
                            helperText="Prefix match, e.g., /api/v1"
                          />
                        </div>

                        {rule.action.type === 'Rewrite' && (
                          <div className="space-y-3">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              Rewrite Destination
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              <Input
                                label="Scheme"
                                placeholder="https"
                                value={rule.action.scheme ?? ''}
                                onChange={(e) =>
                                  updateRichRule(index, (r) => ({
                                    ...r,
                                    action: { ...r.action, scheme: e.target.value || undefined },
                                  }))
                                }
                                helperText="e.g., https"
                              />
                              <Input
                                label="Host"
                                placeholder="api.openai.com"
                                value={rule.action.host ?? ''}
                                onChange={(e) =>
                                  updateRichRule(index, (r) => ({
                                    ...r,
                                    action: { ...r.action, host: e.target.value || undefined },
                                  }))
                                }
                                helperText="Target host"
                              />
                              <Input
                                label="Path"
                                placeholder="/v1/completions"
                                value={rule.action.path ?? ''}
                                onChange={(e) =>
                                  updateRichRule(index, (r) => ({
                                    ...r,
                                    action: { ...r.action, path: e.target.value || undefined },
                                  }))
                                }
                                helperText="Target path"
                              />
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              At least one of scheme, host, or path is required.
                            </p>
                          </div>
                        )}

                        {(rule.action.type === 'Transform' || rule.action.type === 'Rewrite') && (
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                Header Transforms
                              </div>
                              <Button
                                variant="secondary"
                                size="sm"
                                leftIcon={<Plus className="h-3 w-3" />}
                                onClick={() => handleAddHeader(index)}
                              >
                                Add Header
                              </Button>
                            </div>

                            {(rule.action.headers ?? []).map((header, hIdx) => (
                              <div
                                key={hIdx}
                                className="p-3 bg-gray-100 dark:bg-gray-900 rounded-lg space-y-2"
                              >
                                <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                                  <div className="md:col-span-3">
                                    <Select
                                      label={hIdx === 0 ? 'Operation' : undefined}
                                      value={header.operation}
                                      onChange={(e) =>
                                        updateHeader(index, hIdx, (h) => ({
                                          ...h,
                                          operation: e.target.value as EgressPolicyHeaderOperation,
                                        }))
                                      }
                                      options={[
                                        { value: 'Set', label: 'Set' },
                                        { value: 'Insert', label: 'Insert (if absent)' },
                                        { value: 'Remove', label: 'Remove' },
                                      ]}
                                    />
                                  </div>
                                  <div className="md:col-span-4">
                                    <Input
                                      label={hIdx === 0 ? 'Header Name' : undefined}
                                      placeholder="Authorization"
                                      value={header.name}
                                      onChange={(e) =>
                                        updateHeader(index, hIdx, (h) => ({
                                          ...h,
                                          name: e.target.value,
                                        }))
                                      }
                                      required
                                    />
                                  </div>
                                  {header.operation !== 'Remove' &&
                                    getValueSource(header) === 'plain' && (
                                      <div className="md:col-span-4">
                                        <Input
                                          label={hIdx === 0 ? 'Value' : undefined}
                                          placeholder="Bearer my-token"
                                          value={header.value ?? ''}
                                          onChange={(e) =>
                                            updateHeader(index, hIdx, (h) => ({
                                              ...h,
                                              value: e.target.value,
                                              valueRef: undefined,
                                            }))
                                          }
                                          required
                                        />
                                      </div>
                                    )}
                                  {header.operation !== 'Remove' &&
                                    getValueSource(header) === 'secret' && (
                                      <div className="md:col-span-4 flex items-center gap-1.5">
                                        <KeyRound className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                                        <span className="text-xs text-amber-700 dark:text-amber-400 truncate">
                                          {header.valueRef?.secretRef?.secretId || 'Secret ref'}
                                          {header.valueRef?.secretRef?.secretKey
                                            ? ` → ${header.valueRef.secretRef.secretKey}`
                                            : ''}
                                        </span>
                                      </div>
                                    )}
                                  <div
                                    className={`md:col-span-1 flex items-start justify-end ${hIdx === 0 ? 'pt-7' : 'pt-1'}`}
                                  >
                                    <Button
                                      variant="secondary"
                                      size="sm"
                                      onClick={() => handleRemoveHeader(index, hIdx)}
                                      title="Remove header"
                                      aria-label="Remove header"
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>

                                {/* Value source toggle and secret ref fields */}
                                {header.operation !== 'Remove' && (
                                  <div className="space-y-2 pt-1">
                                    <div className="flex items-center gap-4">
                                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                        Value source:
                                      </span>
                                      <label className="flex items-center gap-1.5 cursor-pointer">
                                        <input
                                          type="radio"
                                          name={`value-source-${index}-${hIdx}`}
                                          checked={getValueSource(header) === 'plain'}
                                          onChange={() =>
                                            updateHeader(index, hIdx, (h) => ({
                                              ...h,
                                              value: '',
                                              valueRef: undefined,
                                            }))
                                          }
                                          className="h-3 w-3 text-blue-600"
                                        />
                                        <span className="text-xs text-gray-700 dark:text-gray-300">
                                          Plain text
                                        </span>
                                      </label>
                                      <label className="flex items-center gap-1.5 cursor-pointer">
                                        <input
                                          type="radio"
                                          name={`value-source-${index}-${hIdx}`}
                                          checked={getValueSource(header) === 'secret'}
                                          onChange={() =>
                                            updateHeader(index, hIdx, (h) => ({
                                              ...h,
                                              value: undefined,
                                              valueRef: { secretRef: { secretId: '' } },
                                            }))
                                          }
                                          className="h-3 w-3 text-blue-600"
                                        />
                                        <span className="text-xs text-gray-700 dark:text-gray-300">
                                          Secret reference
                                        </span>
                                      </label>
                                    </div>
                                    {getValueSource(header) === 'secret' && (
                                      <SecretRefFields
                                        header={header}
                                        availableSecrets={availableSecrets}
                                        sandboxGroupId={sandboxGroupId}
                                        onChange={(h) => updateHeader(index, hIdx, () => h)}
                                        showLabels={hIdx === 0}
                                      />
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}

                            {(!rule.action.headers || rule.action.headers.length === 0) && (
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {rule.action.type === 'Transform'
                                  ? 'Transform rules require at least one header transform.'
                                  : 'Optionally add headers to inject into rewritten requests.'}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
